import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Code2, LineChart, Briefcase, Home, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2667">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-2668" />
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-2669" />
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-2670" />
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-2671" />
    </svg>;
}
const FUNCTIONS = [{
  id: 'marketing',
  icon: Megaphone,
  name: 'Growth & Marketing',
  status: 'Live',
  desc: 'Campaign recaps, attribution, and spend monitoring.',
  scenario: {
    title: 'Consolidate data from multiple ad accounts',
    user: {
      name: 'Maya Chen',
      avatar: 'https://i.pravatar.cc/100?img=44',
      time: '11:37 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-2672">
              @Giga
            </span>{' '}
            weekly recap for Q1 campaigns. Spend, ROAS, pipeline generated.
            Branded PDF in{' '}
            <span className="text-[#EA580C]" data-id="element-2673">#marketing-leadership</span>.
          </>
    },
    giga: {
      time: '11:38 AM',
      message: <div className="space-y-3" data-id="element-2674">
            <p data-id="element-2675">Done.</p>
            <ul className="space-y-1" data-id="element-2676">
              <li data-id="element-2677">• Spend: $184k (Google $112k / LinkedIn $72k)</li>
              <li data-id="element-2678">• Pipeline generated: $1.1M (6x ROAS)</li>
              <li data-id="element-2679">• Closed-won revenue: $312k so far</li>
            </ul>
            <p data-id="element-2680">
              PDF in{' '}
              <span className="text-[#EA580C]" data-id="element-2681">#marketing-leadership</span>.
              Schedule every Monday?
            </p>
          </div>,
      reactions: [{
        emoji: '✅',
        count: 2
      }]
    }
  }
}, {
  id: 'sales',
  icon: Briefcase,
  name: 'Sales & Success',
  status: 'Live',
  desc: 'Meeting prep, CRM hygiene, and follow-ups.',
  scenario: {
    title: 'Prep for a high-stakes negotiation call',
    user: {
      name: 'Jordan Reyes',
      avatar: 'https://i.pravatar.cc/100?img=68',
      time: '2:14 PM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-2682">
              @Giga
            </span>{' '}
            prep me for the Acme call tomorrow. Pull the deal stage, recent
            emails, and the latest proposal.
          </>
    },
    giga: {
      time: '2:15 PM',
      message: <div className="space-y-3" data-id="element-2683">
            <p data-id="element-2684">Done. Briefing ready:</p>
            <ul className="space-y-1" data-id="element-2685">
              <li data-id="element-2686">• Deal: $84k ARR (Negotiation stage)</li>
              <li data-id="element-2687">• Room: Eunice (champion), Oleg (CFO)</li>
              <li data-id="element-2688">• Talking point: SOC 2 lands April 15</li>
            </ul>
            <p data-id="element-2689">Want me to draft a 2-month pilot option for Oleg?</p>
          </div>,
      reactions: [{
        emoji: '🔥',
        count: 3
      }]
    }
  }
}, {
  id: 'engineering',
  icon: Code2,
  name: 'Engineering',
  status: 'Live',
  desc: 'Error triage, incident response, and PR reviews.',
  scenario: {
    title: 'Triage incoming production errors',
    user: {
      name: 'Sarah Chen',
      avatar: 'https://i.pravatar.cc/100?img=47',
      time: '9:02 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-2690">
              @Giga
            </span>{' '}
            triage the new Sentry errors from last night's deploy. Group by root
            cause and create Linear tickets.
          </>
    },
    giga: {
      time: '9:03 AM',
      message: <div className="space-y-3" data-id="element-2691">
            <p data-id="element-2692">Done. Triaged 14 errors into 3 tickets:</p>
            <ul className="space-y-1" data-id="element-2693">
              <li data-id="element-2694">• ENG-842: Auth token expiration edge case (High)</li>
              <li data-id="element-2695">• ENG-843: Missing translation keys in onboarding (Low)</li>
              <li data-id="element-2696">• ENG-844: Redis timeout on heavy exports (Med)</li>
            </ul>
            <p data-id="element-2697">Tickets created in Linear. Ping the on-call for ENG-842?</p>
          </div>,
      reactions: [{
        emoji: '🙌',
        count: 4
      }]
    }
  }
}, {
  id: 'finance',
  icon: LineChart,
  name: 'Finance & Ops',
  status: 'Live',
  desc: 'Reconciliation, vendor spend, and board metrics.',
  scenario: {
    title: 'Reconcile daily payouts to accounting',
    user: {
      name: 'Oleg Petrov',
      avatar: 'https://i.pravatar.cc/100?img=33',
      time: '8:30 AM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-2698">
              @Giga
            </span>{' '}
            reconcile yesterday's Stripe payouts to Xero. Flag any mismatched
            amounts or missing invoices.
          </>
    },
    giga: {
      time: '8:32 AM',
      message: <div className="space-y-3" data-id="element-2699">
            <p data-id="element-2700">Done.</p>
            <ul className="space-y-1" data-id="element-2701">
              <li data-id="element-2702">• Matched: 142 transactions ($42,150)</li>
              <li data-id="element-2703">• Mismatched: 0</li>
              <li data-id="element-2704">• Missing invoices: 2 (Acme Corp, Globex)</li>
            </ul>
            <p data-id="element-2705">I drafted emails to Acme and Globex. Review drafts?</p>
          </div>,
      reactions: [{
        emoji: '📈',
        count: 2
      }]
    }
  }
}, {
  id: 'personal',
  icon: Home,
  name: 'Personal & Life',
  status: 'Live',
  channel: '#life-admin',
  desc: 'Travel, errands, reservations, and the loose ends of daily life.',
  scenario: {
    title: 'Hand off the personal logistics',
    user: {
      name: 'Maya Chen',
      avatar: 'https://i.pravatar.cc/100?img=44',
      time: '6:48 PM',
      message: <>
            <span className="text-[#EA580C] bg-[#EA580C]/10 px-1 rounded" data-id="element-2706">
              @Giga
            </span>{' '}
            book my Friday SF trip, find a dinner spot for 4 near the office,
            and reschedule my dentist for next week.
          </>
    },
    giga: {
      time: '6:49 PM',
      message: <div className="space-y-3" data-id="element-2707">
            <p data-id="element-2708">Done.</p>
            <ul className="space-y-1" data-id="element-2709">
              <li data-id="element-2710">• Flight: SFO Fri 9:10 AM, window seat — $382</li>
              <li data-id="element-2711">• Dinner: Rich Table, 7:30 PM for 4 (confirmed)</li>
              <li data-id="element-2712">• Dentist: moved to Tue 4:00 PM</li>
            </ul>
            <p data-id="element-2713">Added everything to your calendar. Want a car to SFO too?</p>
          </div>,
      reactions: [{
        emoji: '🙏',
        count: 2
      }]
    }
  }
}];
export function StartingPointSelector() {
  const [activeId, setActiveId] = useState(FUNCTIONS[0].id);
  const activeFunction = FUNCTIONS.find(f => f.id === activeId)!;
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-28 overflow-hidden" data-id="element-2714">
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-2715">
        {/* Section heading */}
        <div className="max-w-2xl mb-12 md:mb-16" data-id="element-2716">
          <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4 block" data-id="element-2717">
            Choose a starting point
          </span>
          <h2 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight leading-[1.05]" data-id="element-2718">
            Start with the function that eats the most time.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-16 items-start" data-id="element-2719">
          {/* LEFT: Selector List */}
          <div className="flex flex-col" data-id="element-2720">
            <div className="flex flex-col gap-1.5 mb-8" data-id="element-2721">
              {FUNCTIONS.map(func => {
              const isActive = activeId === func.id;
              const Icon = func.icon;
              return <button key={func.id} onClick={() => setActiveId(func.id)} aria-pressed={isActive} className={`group relative text-left w-full rounded-2xl transition-all duration-300 ${isActive ? 'bg-white shadow-[0_8px_24px_-12px_rgba(43,33,26,0.15)]' : 'bg-transparent hover:bg-white/50'}`} data-id="element-2722">
                    <div className="px-5 py-4 flex items-center gap-4" data-id="element-2723">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${isActive ? 'bg-[#EA580C] text-white' : 'bg-white text-neutral-400 group-hover:text-neutral-600'}`} data-id="element-2724">
                        <Icon className="w-[18px] h-[18px]" data-id="element-2725" />
                      </div>
                      <span className={`text-[16px] font-bold transition-colors ${isActive ? 'text-[#2B211A]' : 'text-neutral-500 group-hover:text-[#2B211A]'}`} data-id="element-2726">
                        {func.name}
                      </span>

                      <div className="ml-auto shrink-0" data-id="element-2727">
                        <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-colors ${isActive ? 'border-[#EA580C]' : 'border-neutral-300 group-hover:border-neutral-400'}`} data-id="element-2728">
                          {isActive && <motion.div layoutId="sp-radio-indicator" className="w-2 h-2 rounded-full bg-[#EA580C]" data-id="element-2729" />}
                        </div>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            <Link to="/signup" className="inline-flex items-center justify-center bg-[#2B211A] hover:bg-[#1A1410] text-white px-7 py-3.5 rounded-full font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 w-full sm:w-auto self-start" data-id="element-2730">
              Get Started for Free
            </Link>
          </div>

          {/* RIGHT: Slack Mockup */}
          <div className="relative w-full" data-id="element-2731">
            <div className="absolute inset-0 bg-[#EA580C]/[0.06] blur-[100px] rounded-full pointer-events-none" data-id="element-2732" />

            <div className="relative bg-white rounded-[28px] border border-[#EAE4D9] shadow-[0_32px_64px_-20px_rgba(43,33,26,0.12)] overflow-hidden flex flex-col min-h-[460px]" data-id="element-2733">
              {/* Slack-style channel header */}
              <div className="px-6 py-4 border-b border-[#EAE4D9] flex items-center gap-2.5" data-id="element-2734">
                <SlackIcon size={16} data-id="element-2735" />
                <span className="text-sm font-bold text-[#2B211A]" data-id="element-2736">
                  {(activeFunction as any).channel || '#leadership'}
                </span>
                <span className="text-xs text-neutral-400 font-medium" data-id="element-2737">
                  · Slack
                </span>
              </div>

              <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-2738">
                <AnimatePresence mode="wait" data-id="element-2739">
                  <motion.div key={activeId} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.3
                }} className="flex flex-col gap-7" data-id="element-2740">
                    {/* User Message */}
                    <div className="flex gap-3.5" data-id="element-2741">
                      <img src={activeFunction.scenario.user.avatar} alt="" className="w-9 h-9 rounded-lg shrink-0 object-cover" data-id="element-2742" />
                      <div className="flex flex-col gap-1" data-id="element-2743">
                        <div className="flex items-baseline gap-2" data-id="element-2744">
                          <span className="font-bold text-[#2B211A] text-[14px]" data-id="element-2745">
                            {activeFunction.scenario.user.name}
                          </span>
                          <span className="text-xs text-neutral-400" data-id="element-2746">
                            {activeFunction.scenario.user.time}
                          </span>
                        </div>
                        <div className="text-[14px] text-[#2B211A] leading-relaxed" data-id="element-2747">
                          {activeFunction.scenario.user.message}
                        </div>
                      </div>
                    </div>

                    {/* Giga Reply */}
                    <div className="flex gap-3.5" data-id="element-2748">
                      <div className="w-9 h-9 rounded-lg bg-[#EA580C] shrink-0 flex items-center justify-center" data-id="element-2749">
                        <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_saturate(100%)_invert(100%)]" data-id="element-2750" />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0" data-id="element-2751">
                        <div className="flex items-baseline gap-2" data-id="element-2752">
                          <span className="font-bold text-[#2B211A] text-[14px]" data-id="element-2753">
                            Giga
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 uppercase tracking-wider" data-id="element-2754">
                            App
                          </span>
                          <span className="text-xs text-neutral-400" data-id="element-2755">
                            {activeFunction.scenario.giga.time}
                          </span>
                        </div>

                        <div className="bg-[#FAF8F4] border border-[#EAE4D9] rounded-2xl rounded-tl-sm p-4 text-[14px] text-[#2B211A] leading-relaxed mt-0.5" data-id="element-2756">
                          {activeFunction.scenario.giga.message}
                        </div>

                        <div className="flex gap-2 mt-1.5" data-id="element-2757">
                          {activeFunction.scenario.giga.reactions.map((reaction, i) => <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white border border-[#EAE4D9] text-xs font-medium text-neutral-600" data-id="element-2758">
                                <span data-id="element-2759">{reaction.emoji}</span>
                                <span data-id="element-2760">{reaction.count}</span>
                              </div>)}
                          <div className="flex items-center justify-center w-6.5 h-6.5 rounded-full bg-white border border-[#EAE4D9] text-neutral-400 px-1.5 py-1" data-id="element-2761">
                            <Plus size={13} data-id="element-2762" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}